import axios from 'axios';
import { setupCache } from 'axios-cache-interceptor';
import { sanitizeData } from '../utils/sanitize.js';

// Get API URL from environment variable or use default
const API_URL = import.meta.env.VITE_API_URL || 'https://inkword-backend.onrender.com/api';

// Create base axios instance
const axiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true // Enable if using cookies
});

// Add request throttling
let lastRequestTime = 0;
const minRequestInterval = 1000; // 1 second minimum between requests

axiosInstance.interceptors.request.use(async (config) => {
  const now = Date.now();
  const timeToWait = Math.max(0, minRequestInterval - (now - lastRequestTime));
  
  if (timeToWait > 0) {
    await new Promise(resolve => setTimeout(resolve, timeToWait));
  }
  
  lastRequestTime = Date.now();
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // For file uploads, increase the timeout
  if (config.headers['Content-Type']?.includes('multipart/form-data')) {
    config.timeout = 60000; // 60 seconds for file uploads
  }
  return config;
});

// Add retry logic
axiosInstance.interceptors.response.use(
  response => response,
  async error => {
    const { config, response } = error;
    
    if (response?.status === 429 && !config._retry) {
      config._retry = true;
      await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds before retry
      return axiosInstance(config);
    }
    
    if (error.code === 'ERR_NETWORK') {
      console.error('Network error - Please check if the backend server is running');
    }
    return Promise.reject(error);
  }
);

// Add caching for GET requests
const apiClient = setupCache(axiosInstance, {
  ttl: 1000 * 60 * 5, // Cache for 5 minutes
  methods: ['get'],
  cacheTakeover: false,
  headers: {
    'X-Content-Type-Options': 'nosniff',
    'X-Frame-Options': 'DENY',
    'X-XSS-Protection': '1; mode=block'
  }
});

// Add response interceptor for handling rate limiting
apiClient.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 429) {
      // Get retry-after header if available
      const retryAfter = error.response.headers['retry-after'];
      if (retryAfter) {
        console.warn(`Rate limited. Retry after ${retryAfter} seconds`);
      }
    }
    return Promise.reject(error);
  }
);

// Add request interceptor to add auth token
apiClient.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Skip sanitization for FormData
    if (!(config.data instanceof FormData)) {
      // Sanitize request data
      config.data = sanitizeData(config.data);
    }
    
    // Add CSRF token if available
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }
    
    return config;
  },
  error => Promise.reject(error)
);

export { apiClient }; 
